import React, { useState } from 'react';
import { DetailedSaleItem } from '@src/core/buyerApiClient';
import {
  SaleItem as SaleItemWrapper,
  SaleItemInfo,
  ProductName,
  SaleItemLabelValue,
  AlertContentWrapper,
} from './styles';
import ProductImage from '../ProductImage';
import useTranslate from '@src/i18n/useTranslate';
import useCurrencyFormat from '@src/hooks/useCurrencyFormat';
import { formatItemSchedule } from '@src/common/utils/formatItemSchedule';
import Alert from '@src/components/reusable/Alert';
import { IconX, IconAlertCircle } from '@tabler/icons-react';

interface SaleItemComponentProps {
  item: DetailedSaleItem;
  showPlaceholderImage?: boolean;
  children?: React.ReactNode;
}

export const SaleItem: React.FC<SaleItemComponentProps> = ({ item, showPlaceholderImage = false, children }) => {
  const { i18n } = useTranslate();
  const { formatCurrencyInfo } = useCurrencyFormat();
  const [openAlert, setOpenAlert] = useState(true);

  return (
    <SaleItemWrapper key={item.id}>
      <ProductImage product={item.product} showPlaceholderImage={showPlaceholderImage} />

      <SaleItemInfo>
        <ProductName>{item.product.name}</ProductName>

        <SaleItemLabelValue>
          <p>{i18n('direct_link_summary_date_and_time')}</p>
          <p>
            {formatItemSchedule(item, {
              datePrefix: i18n('checkout_purchase_summary_day'),
              fallbackText: i18n('checkout_purchase_summary_without_date'),
            })}
          </p>
        </SaleItemLabelValue>

        {item.tariffs.map((tariff) => (
          <SaleItemLabelValue key={tariff.id}>
            <p>
              {tariff.name} - {tariff.quantity}
            </p>
            <p>{formatCurrencyInfo(tariff.unitPriceCurrencyInfo)}</p>
          </SaleItemLabelValue>
        ))}

        {item?.resources.map((saleResource) => (
          <SaleItemLabelValue key={saleResource.resource.name}>
            <p>{saleResource.resource.formattedName}</p>
            <p>{saleResource.priceCurrencyInfo ? formatCurrencyInfo(saleResource.priceCurrencyInfo) : null}</p>
          </SaleItemLabelValue>
        ))}

        {item.additionals.map((saleAdditional) => (
          <SaleItemLabelValue key={saleAdditional.additional.id}>
            <p>
              {saleAdditional.additional.title} - {saleAdditional.unitQuantity}
            </p>

            <p>{formatCurrencyInfo(saleAdditional.priceCurrencyInfo)}</p>
          </SaleItemLabelValue>
        ))}

        {children}
      </SaleItemInfo>

      {item.productPackageOriginalDiscountCurrencyInfo &&
      Number.isFinite(item.productPackageOriginalDiscountCurrencyInfo.amountCents) &&
      openAlert ? (
        <Alert
          severity='warning'
          sx={{ background: '#FDE9CE', color: '#945605', width: '100%' }}
          icon={<IconAlertCircle size={24} />}
        >
          <AlertContentWrapper>
            <p>{i18n('discount_fallback_warning_message')}</p>
            <IconX size={24} onClick={() => setOpenAlert(false)} />
          </AlertContentWrapper>
        </Alert>
      ) : null}
    </SaleItemWrapper>
  );
};

export default SaleItem;
